import { Fragment, type CSSProperties } from 'react';
import type { LibraryStatsKey } from '@/music/libraryStats';
import { dotDiameter, keyWheelSlots, slotPosition, type WheelSlot } from './keyWheel';

const MAJOR_RING = 33;
const MINOR_RING = 19;
const MAJOR_LABEL = 45;
const MINOR_LABEL = 12;

const tunesLabel = (n: number) => `${n.toLocaleString('en-GB')} ${n === 1 ? 'tune' : 'tunes'}`;

/**
 * The library's keys on the circle of fifths. Each key with tunes is a button
 * whose dot's area follows its count; pressing it hands the slot to `onSelect`
 * (the home page lists the tunes). Labels sit outside the majors and inside
 * the minors, so neither ring crowds the other.
 */
export function KeyWheel({
  keys,
  selected,
  onSelect,
}: {
  keys: readonly LibraryStatsKey[];
  /** The `name` of the slot whose tunes are open, if any. */
  selected: string | null;
  onSelect: (slot: WheelSlot) => void;
}) {
  const slots = keyWheelSlots(keys);
  const max = Math.max(1, ...slots.map((slot) => slot.tunes.length));
  const top = (minor: boolean, count: number) =>
    slots
      .filter((slot) => slot.minor === minor && slot.tunes.length > 0)
      .sort((a, b) => b.tunes.length - a.tunes.length)
      .slice(0, count)
      .map((slot) => `${minor ? slot.short.toUpperCase() + 'm' : slot.short} ${slot.tunes.length}`)
      .join(' · ');

  return (
    <>
      <div className="key-wheel" data-testid="key-wheel">
        <svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">
          <circle className="key-wheel-ring" cx="50" cy="50" r={MAJOR_RING} />
          <circle className="key-wheel-ring" cx="50" cy="50" r={MINOR_RING} />
        </svg>
        {slots.map((slot) => {
          const count = slot.tunes.length;
          const id = `${slot.minor ? 'minor' : 'major'}-${slot.slot}`;
          return (
            <Fragment key={id}>
              {count > 0 && (
                <button
                  type="button"
                  className={`key-wheel-dot${slot.minor ? ' minor' : ''}`}
                  style={
                    {
                      ...slotPosition(slot.slot, slot.minor ? MINOR_RING : MAJOR_RING),
                      '--d': dotDiameter(count, max),
                    } as CSSProperties
                  }
                  aria-label={`${slot.name}, ${tunesLabel(count)}`}
                  aria-expanded={selected === slot.name}
                  title={`${slot.name}: ${tunesLabel(count)}`}
                  data-testid="key-wheel-dot"
                  data-key={slot.name}
                  onClick={() => onSelect(slot)}
                />
              )}
              <span
                className={`key-wheel-label${slot.minor ? ' minor' : ''}${count ? '' : ' empty'}`}
                style={slotPosition(slot.slot, slot.minor ? MINOR_LABEL : MAJOR_LABEL)}
                aria-hidden="true"
              >
                {slot.short}
              </span>
            </Fragment>
          );
        })}
      </div>
      <p className="key-wheel-legend">
        <span>
          <i aria-hidden="true" />
          Major
        </span>
        <span>
          <i className="minor" aria-hidden="true" />
          Minor
        </span>
      </p>
      <p className="key-wheel-top">
        {top(false, 5)}
        <br />
        {top(true, 4)}
      </p>
    </>
  );
}
